// MSM shift plans: saved Prior-Day / Pre-Shift plan versions + plan-vs-actual by quarter.
// Shared by the MSM tab (save/view), Narcs'r'us (weekly % to plan) and mirrored by
// backend/msm-slack.sh (Slack checkpoints). Keep the three in sync.
//
// Cache records (POST /api/fclm/cache is a full put, so every version is its own record
// and the index is small):
//   MSM_PLANV_<date> / <shift>|<kind>|<n>  -> one saved plan version (inputs + pinned
//                                            LVP/shape + summary)
//   MSM_PLANS_<date> / <shift>             -> index { versions: [meta], priorDay: meta+summary,
//                                            preShift: meta+summary }
// kind = 'priorDay' | 'preShift'.

// OLM1 quarter windows (MSM is OLM1-only). paidHrs match the cached FCLM schedule.
export const QUARTERS = [
  { id: 'Q1', dayWindow: '08:00–10:00', nightWindow: '19:00–21:00', paidHrs: 2.0,
    dayLabels: ['08:00', '09:00'], nightLabels: ['19:00', '20:00'] },
  { id: 'Q2', dayWindow: '10:00–13:00', nightWindow: '21:00–00:00', paidHrs: 2.75,
    dayLabels: ['10:00', '11:00', '12:00'], nightLabels: ['21:00', '22:00', '23:00'] },
  { id: 'Q3', dayWindow: '13:00–16:00', nightWindow: '00:00–03:00', paidHrs: 2.75,
    dayLabels: ['13:00', '14:00', '15:00'], nightLabels: ['00:00', '01:00', '02:00'] },
  { id: 'Q4', dayWindow: '16:00–18:30', nightWindow: '03:00–05:30', paidHrs: 2.5,
    dayLabels: ['16:00', '17:00', '18:00'], nightLabels: ['03:00', '04:00', '05:00'] },
];
export const QIDS = ['Q1', 'Q2', 'Q3', 'Q4'];

export const PLAN_KINDS = { priorDay: 'Prior-Day', preShift: 'Pre-Shift' };

const indexKey = (date) => `MSM_PLANS_${date}`;
const versionKey = (date) => `MSM_PLANV_${date}`;
const versionShift = (shift, kind, n) => `${shift}|${kind}|${n}`;

const getJson = (url) => fetch(url).then(r => r.json()).catch(() => null);

export async function loadPlanIndex(api, site, date, shift) {
  const d = await getJson(`${api}/api/fclm/cache?site=${site}&date=${indexKey(date)}&shift_type=${shift}`);
  return d && Array.isArray(d.versions) ? d : { versions: [] };
}

export async function loadPlanVersion(api, site, date, shift, kind, n) {
  const d = await getJson(`${api}/api/fclm/cache?site=${site}&date=${versionKey(date)}&shift_type=${encodeURIComponent(versionShift(shift, kind, n))}`);
  return d && d.kind ? d : null;
}

const post = (api, body) => fetch(`${api}/api/fclm/cache`, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
}).then(r => { if (!r.ok) throw new Error(`save failed (${r.status})`); return r; });

// Save a new version of `kind`. The version record is written first; the index is
// re-read immediately before writing so a concurrent save is not dropped.
export async function savePlanVersion(api, site, date, shift, kind, snapshot) {
  const idx0 = await loadPlanIndex(api, site, date, shift);
  const n = idx0.versions.filter(v => v.kind === kind).reduce((m, v) => Math.max(m, v.n || 0), 0) + 1;
  const meta = { kind, n, savedAt: new Date().toISOString(), savedBy: snapshot.savedBy || '' };
  await post(api, { site, date: versionKey(date), shift_type: versionShift(shift, kind, n), data: { ...snapshot, ...meta } });
  const idx = await loadPlanIndex(api, site, date, shift);
  const versions = [...idx.versions.filter(v => !(v.kind === kind && v.n === n)), meta]
    .sort((a, b) => String(a.savedAt).localeCompare(String(b.savedAt)));
  await post(api, { site, date: indexKey(date), shift_type: shift, data: { ...idx, versions, [kind]: { ...meta, summary: snapshot.summary } } });
  return meta;
}

// The plan Ops measure against: latest Pre-Shift, else latest Prior-Day.
export function activePlan(index) {
  if (!index) return null;
  if (index.preShift && index.preShift.summary) return index.preShift;
  if (index.priorDay && index.priorDay.summary) return index.priorDay;
  return null;
}

const THROUGHPUT = 'ppr.fcSummary.throughput'; // hours = IB + Transfer Out + Support (PPR)
// Cartons = FCLM inbound + outbound cartons per hour (the plan is in IB + OB cartons, as LVP).
// PPR throughput *volume* is units, not cartons (~1.7x cartons at OLM1), so it is not used.

// Actual hours / cartons per quarter from a cached FCLM day|night record.
export function actualsByQuarter(fclm, shift) {
  const line = fclm && fclm.ppr_lines && fclm.ppr_lines[THROUGHPUT];
  if (!line) return null;
  const ctn = (k, lbl) => parseFloat(fclm?.cartons?.[k]?.hours?.[lbl]) || 0;
  const out = {}; let any = false;
  QUARTERS.forEach(Q => {
    let h = 0, c = 0, seen = 0;
    (shift === 'day' ? Q.dayLabels : Q.nightLabels).forEach(lbl => {
      const v = line[lbl];
      if (Array.isArray(v)) { h += parseFloat(v[1]) || 0; if ((parseFloat(v[1]) || 0) > 0) seen += 1; }
      c += ctn('inbound', lbl) + ctn('outbound', lbl);
    });
    out[Q.id] = { hours: h, cartons: c, hoursSeen: seen };
    if (h > 0) any = true;
  });
  return any ? out : null;
}

const ratio = (a, b) => (b > 0 && a != null ? a / b : null);

// % to plan for one quarter (or the shift when q === 'shift').
//   hoursPct = actual hours / plan hours; ratePct = actual CPLH / plan CPLH.
export function pctToPlan(summary, actuals, q) {
  if (!summary || !actuals) return null;
  // 'shift' = every quarter that has actuals so far (an in-progress shift isn't judged on future quarters)
  const ids = q === 'shift' ? QIDS.filter(k => (actuals[k]?.hours || 0) > 0) : [q];
  const p = ids.reduce((t, k) => ({ hours: t.hours + (summary.quarters?.[k]?.hours || 0), cartons: t.cartons + (summary.quarters?.[k]?.cartons || 0) }), { hours: 0, cartons: 0 });
  const a = ids.reduce((t, k) => ({ hours: t.hours + (actuals[k]?.hours || 0), cartons: t.cartons + (actuals[k]?.cartons || 0) }), { hours: 0, cartons: 0 });
  if (!(a.hours > 0)) return null;
  return {
    plan: p, actual: a,
    hoursPct: ratio(a.hours, p.hours),
    cartonsPct: ratio(a.cartons, p.cartons),
    ratePct: ratio(ratio(a.cartons, a.hours), ratio(p.cartons, p.hours)),
  };
}
